/**
 * @file viewportUtils.js
 * @description Viewport utilities for handling mobile browser address bar and PWA detection
 */

import { useState, useEffect } from 'react';

/**
 * Detect if the app is running as an installed PWA
 * @returns {boolean} True if running as installed PWA
 */
export const isPWAInstalled = () => {
  try {
    return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
           (window.navigator && window.navigator.standalone === true);
  } catch (error) {
    // Fallback for test environments or browsers without matchMedia support
    return false;
  }
};

/**
 * Calculate the appropriate viewport height based on device and installation status
 * @returns {string} CSS height value
 */
export const getViewportHeight = () => {
  try {
    const isInstalled = isPWAInstalled();
    const isMobile = typeof navigator !== 'undefined' && 
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isInstalled) {
      // Use full viewport for installed PWA
      return '100vh';
    } else if (isMobile && typeof window !== 'undefined' && window.visualViewport) {
      // Use visual viewport for mobile browsers to account for address bar
      return `${window.visualViewport.height}px`;
    } else if (isMobile) {
      // Fallback for mobile browsers without visual viewport support
      return 'calc(100vh - 60px)';
    } else {
      // Desktop browsers
      return '100vh';
    }
  } catch (error) {
    // Fallback for test environments
    return '100vh';
  }
};

/**
 * React hook for dynamic viewport height that updates when browser UI changes
 * @returns {string} Current viewport height CSS value
 */
export const useViewportHeight = () => {
  const [viewportHeight, setViewportHeight] = useState(getViewportHeight());

  useEffect(() => {
    const updateViewportHeight = () => {
      setViewportHeight(getViewportHeight());
    };

    // Only add event listeners in browser environment
    if (typeof window !== 'undefined') {
      try {
        // Listen for visual viewport changes (mobile browser address bar show/hide)
        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', updateViewportHeight);
        }

        // Listen for window resize as fallback
        window.addEventListener('resize', updateViewportHeight);

        // Listen for PWA installation
        const handleAppInstalled = () => {
          updateViewportHeight();
        };
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
          try {
            if (window.visualViewport) {
              window.visualViewport.removeEventListener('resize', updateViewportHeight);
            }
            window.removeEventListener('resize', updateViewportHeight);
            window.removeEventListener('appinstalled', handleAppInstalled);
          } catch (error) {
            // Ignore cleanup errors in test environments
          }
        };
      } catch (error) {
        // Ignore event listener errors in test environments
      }
    }
  }, []);

  return viewportHeight;
};
